import { Injectable, computed, effect, inject, signal, untracked, DOCUMENT } from '@angular/core';
import { SupportAccessService } from './support-access.service';
import { SupportApiService } from './support-api.service';

/**
 * Ongelezen-tellers voor de handle en de tabs. Fouten blijven stil: de tellers
 * zijn een hint, geen bron van waarheid, dus geen toast en geen console-ruis.
 */
@Injectable({ providedIn: 'root' })
export class SupportBadgeService {
  private readonly access = inject(SupportAccessService);
  private readonly api = inject(SupportApiService);
  private readonly document = inject(DOCUMENT);

  readonly mineUnread = signal(0);
  readonly manageUnread = signal(0);
  readonly total = computed(() => this.mineUnread() + (this.access.canManage() ? this.manageUnread() : 0));

  private inFlight?: Promise<void>;

  constructor() {
    effect(() => {
      const canUse = this.access.canUse();
      untracked(() => {
        if (!canUse) {
          this.reset();
          return;
        }
        void this.refresh();
      });
    });

    this.document.addEventListener('visibilitychange', () => {
      if (this.document.visibilityState !== 'visible' || !this.access.canUse()) return;
      void this.refresh();
    });
  }

  /** Overlappende aanroepen delen dezelfde lopende ophaalronde. */
  refresh(): Promise<void> {
    if (this.inFlight) return this.inFlight;
    this.inFlight = this.fetchCounts().finally(() => {
      this.inFlight = undefined;
    });
    return this.inFlight;
  }

  private async fetchCounts(): Promise<void> {
    const [mine, manage] = await Promise.all([
      this.tryFetch(() => this.api.unreadMine()),
      this.access.canManage() ? this.tryFetch(() => this.api.unreadManage()) : Promise.resolve(undefined),
    ]);
    // Intussen uitgelogd: geen verouderde waarde meer zetten.
    if (!this.access.canUse()) {
      this.reset();
      return;
    }
    if (mine !== undefined) this.mineUnread.set(mine);
    if (manage !== undefined) this.manageUnread.set(manage);
  }

  /** Fout of geen resultaat: undefined, zodat de teller de vorige waarde houdt. */
  private async tryFetch(call: () => Promise<number>): Promise<number | undefined> {
    try {
      return await call();
    } catch {
      return undefined;
    }
  }

  private reset(): void {
    this.mineUnread.set(0);
    this.manageUnread.set(0);
  }
}
